import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import { type IamApi } from '@/features/iam/api/iam-api'
import { clearMemoized } from '@/utils/memoize-util'
import { memoizeAsync } from '@/utils/memoize-decorator'
import { UserData, type UserDataType } from './UserData'
import {
  mapUserDataResourceToProps,
  mapUserDataTypeToResource,
} from './user-data-mappers'
import type { FetchUserDataOptions } from '@/features/iam'
import type { Member } from '@/features/iam/members/Member'
import { catchError, EMPTY, type Observable, switchMap } from 'rxjs'
import { shareLatest, withAbortSignal } from '@/utils/rx-util'

export interface UpdateUserDataParams {
  type?: UserDataType
  jobCapacity?: number
}

export class UserDataStore {
  private readonly _userData = collection({
    getDataId: (data) => data.userPublicId,
    getModelId: (model) => model.userPublicId,
    create: () => new UserData(),
  })

  /**
   * Emits the current user's data once a member has been chosen.
   */
  readonly currentUserData$: Observable<UserData>

  constructor(
    currentMember$: Observable<Member>,
    private readonly _api: IamApi,
  ) {
    this.currentUserData$ = currentMember$.pipe(
      switchMap((member) =>
        withAbortSignal((signal) =>
          this.fetchByPublicId(member.user.publicId, {}, signal),
        ).pipe(
          // A failed fetch skips this member rather than ending the stream.
          catchError(() => EMPTY),
        ),
      ),
      shareLatest(),
    )
  }

  /**
   * Gets already-fetched user data for the given user.
   */
  getByPublicId(publicId: Id): UserData | null {
    return this._userData.get(publicId) ?? null
  }

  /**
   * Fetches user data by public ID.
   */
  async fetchByPublicId(
    publicId: Id,
    options: FetchUserDataOptions = {},
    signal?: AbortSignal,
  ): Promise<UserData> {
    if (options.skipCache) {
      clearMemoized(this._fetchByPublicId, publicId)
    }
    return this._fetchByPublicId(publicId, signal)
  }

  /**
   * Updates a user's data, then refreshes it in the background.
   */
  async updateUserData(publicId: Id, params: UpdateUserDataParams) {
    await this._api.userData.update(Id.assert(publicId), {
      type: params.type && mapUserDataTypeToResource(params.type),
      job_capacity: params.jobCapacity,
    })

    void this.fetchByPublicId(publicId, { skipCache: true })
  }

  @memoizeAsync()
  private async _fetchByPublicId(
    publicId: Id,
    signal?: AbortSignal,
  ): Promise<UserData> {
    const data = await this._api.userData.get(Id.assert(publicId), signal)
    return this._userData.set(mapUserDataResourceToProps(data))
  }
}
